import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Info } from "lucide-react";
import { fetchAPI } from "../../config/api";
import { PLAYER_HEADSHOT_FALLBACK } from "../../config/constants";

const Headshot = ({ src, name }) => (
  <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
    <img 
      src={src || PLAYER_HEADSHOT_FALLBACK} 
      alt={name} 
      className="w-full h-full object-cover object-top"
      onError={(e) => (e.currentTarget.src = PLAYER_HEADSHOT_FALLBACK)}
    />
  </div>
);

const getRoleInfo = (gmli) => {
  if (gmli >= 1.5) return { role: "High Leverage", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" };
  if (gmli >= 1.0) return { role: "Setup", color: "text-blue-600", bg: "bg-blue-50", border: "border-blue-200" };
  if (gmli >= 0.7) return { role: "Middle Relief", color: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200" };
  return { role: "Mop-up", color: "text-slate-500", bg: "bg-slate-50", border: "border-slate-200" };
};

const formatIP = (avgOuts) => {
  if (avgOuts == null) return "—";
  const totalOuts = Math.round(avgOuts);
  const innings = Math.floor(totalOuts / 3);
  const partialOuts = totalOuts % 3;
  return `${innings}.${partialOuts}`;
};

const InfoTooltip = ({ children }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-gray-400 hover:text-gray-600 transition-colors"
      >
        <Info size={14} />
      </button>
      {show && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
          {children}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
};

export const PitcherUtilization = ({ orgId, division, years }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(years?.[0] || null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const yearParam = selectedYear ? `&year=${selectedYear}` : "";
        const response = await fetchAPI(`/api/team/${orgId}/pitcher-utilization?division=${division}${yearParam}`);
        setData(response);
        if (!selectedYear && response.available_years?.length) {
          setSelectedYear(response.available_years[0]);
        }
      } catch (err) {
        console.error("Error fetching pitcher utilization:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [orgId, division, selectedYear]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data?.pitchers?.length) {
    return <div className="text-center py-12 text-gray-500">No pitcher data available</div>;
  }

  const totalGames = data.pitchers[0]?.total_games || 1;
  const starterThreshold = Math.ceil(totalGames * 0.1);
  
  const rotation = data.pitchers.filter(p => p.starts >= starterThreshold).slice(0, 5);
  const bullpen = data.pitchers.filter(p => p.starts < starterThreshold && p.games >= 2).sort((a, b) => (b.relief_gmli || b.gmli || 0) - (a.relief_gmli || a.gmli || 0));
  const availableYears = data.available_years || years || [];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900">Pitcher Usage</h2>
        {availableYears.length > 0 && (
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {availableYears.map((yr) => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
        )}
      </div>

      {rotation.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">Rotation</h3>
            <InfoTooltip>
              <p>Pitchers who started at least 10% of team games.</p>
            </InfoTooltip>
          </div>
          <div className="grid gap-2">
            {rotation.map((p, i) => (
              <div
                key={p.pitcher_id}
                onClick={() => navigate(`/player/${p.pitcher_id}`)}
                className="flex items-center gap-4 p-3 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-100 hover:border-blue-200 hover:shadow-sm transition-all cursor-pointer group"
              >
                <span className="text-lg font-bold text-gray-300 w-5">{i + 1}</span>
                <Headshot src={p.img_url} name={p.player_name} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                    {p.player_name}
                  </div>
                  <div className="text-xs text-gray-500">
                    {p.starts} GS • {p.games} G
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-semibold text-gray-900">
                    {formatIP(p.avg_outs_start)}
                  </div>
                  <div className="text-xs text-gray-400">IP/Start</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {bullpen.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-medium text-gray-600 uppercase tracking-wide">Bullpen</h3>
            <InfoTooltip>
              <p className="mb-2"><strong>gmLI</strong> = leverage index when pitcher enters.</p>
              <div className="space-y-1 text-[11px]">
                <div className="flex justify-between"><span className="text-red-400">● High Leverage</span><span>1.50+</span></div>
                <div className="flex justify-between"><span className="text-blue-400">● Setup</span><span>1.00–1.49</span></div>
                <div className="flex justify-between"><span className="text-gray-400">● Middle Relief</span><span>0.70–0.99</span></div>
                <div className="flex justify-between"><span className="text-slate-400">● Mop-up</span><span>&lt;0.70</span></div>
              </div>
            </InfoTooltip>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {bullpen.map((p) => {
              const gmli = p.relief_gmli ?? p.gmli ?? 0;
              const { role, color, bg, border } = getRoleInfo(gmli);
              return (
                <div
                  key={p.pitcher_id}
                  onClick={() => navigate(`/player/${p.pitcher_id}`)}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer group ${bg} ${border} hover:shadow-sm`}
                >
                  <Headshot src={p.img_url} name={p.player_name} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate text-sm">
                      {p.player_name}
                    </div>
                    <div className="text-xs text-gray-500">{p.games} G • <span className={color}>{role}</span></div>
                  </div>
                  <div className={`text-sm font-bold ${color}`}>
                    {gmli.toFixed(2)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
